// pages/BlogPage.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CONSTANTS } from '../constants/Constants';
import Loader from '../components/common/Spinner';

const BlogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialFilter = queryParams.get('category') || 'all';
  
  const [filter, setFilter] = useState(initialFilter);
  const [loading, setLoading] = useState(true);
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [popularTags, setPopularTags] = useState([]);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    loadCategories();
    loadBlogPosts();
  }, []);
  
  useEffect(() => {
    // Update URL when filter changes
    if (filter !== initialFilter) {
      const params = new URLSearchParams(location.search);
      if (filter === 'all') {
        params.delete('category');
      } else {
        params.set('category', filter);
      }
      navigate({ search: params.toString() });
    }
    
    // Apply filter to blog posts
    filterBlogPosts();
  }, [filter, blogPosts]);
  
  const loadCategories = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.APIURL}/api/blog-categories?active=true`);
      
      // Add "All Posts" category
      const allCategories = [
        { _id: 'all', identifier: 'all', name: 'All Posts' },
        ...response.data.data
      ];
      
      setBlogCategories(allCategories);
    } catch (error) {
      console.error('Error fetching blog categories:', error);
      toast.error('Failed to load blog categories');
    }
  };
  
  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      
      // Fetch featured post
      const featuredResponse = await axios.get(`${CONSTANTS.APIURL}/api/blog-posts?featured=true&limit=1`);
      if (featuredResponse.data.data.length > 0) {
        setFeaturedPost(featuredResponse.data.data[0]);
      }
      
      // Fetch all posts
      const response = await axios.get(`${CONSTANTS.APIURL}/api/blog-posts?limit=9`);
      setBlogPosts(response.data.data);
      
      // Extract unique tags for "Popular Topics" section
      const allTags = response.data.data.flatMap(post => post.tags);
      const uniqueTags = [...new Set(allTags)];
      setPopularTags(uniqueTags);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
      setLoading(false);
    }
  };
  
  const filterBlogPosts = () => {
    if (filter === 'all') {
      setFilteredBlogs(blogPosts);
    } else {
      const filtered = blogPosts.filter(post => post.category === filter);
      setFilteredBlogs(filtered);
    }
  };
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    try {
      setSubscribing(true);
      
      await axios.post(`${CONSTANTS.APIURL}/api/blog/subscribe`, { email });
      
      toast.success('Thank you for subscribing to our newsletter!');
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to subscribe. Please try again later.');
      }
    } finally {
      setSubscribing(false);
    }
  };
  
  // Format date helper function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Get category name helper function
  const getCategoryName = (categoryId) => {
    const category = blogCategories.find(cat => cat.identifier === categoryId);
    return category ? category.name : categoryId;
  };
  
  if (loading) {
    return <Loader />;
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#2C5282] opacity-70"></div>
          <div className="absolute inset-0 bg-[url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRiqn5jXhC1CTq8isbIwTTte77VL0U7zhlBwQ&s')] opacity-10"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              SkySep Learning Blog
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl mb-8 text-blue-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Explore expert insights, study tips, and educational resources for students in classes 8-10. Stay ahead with our comprehensive learning materials and expert guidance.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* <Link
                to="/subscribe"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-md"
              >
                Subscribe to Updates
              </Link>
              <Link
                to="/contribute"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
              >
                Contribute an Article
              </Link> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Blog Post */}
      {featuredPost && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Featured Article
              </motion.h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg overflow-hidden shadow-lg"
            >
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8">
                  <div className="uppercase tracking-wide text-sm text-[#2C5282] font-semibold">
                    {getCategoryName(featuredPost.category)}
                  </div>
                  <Link 
                    to={`/blog/${featuredPost.slug}`} 
                    className="block mt-2 text-2xl font-semibold text-[#1A365D] hover:text-[#2C5282] transition-colors duration-200"
                  >
                    {featuredPost.title}
                  </Link>
                  <p className="mt-3 text-gray-600">{featuredPost.excerpt}</p>
                  
                  <div className="mt-4 flex items-center">
                    <div className="flex-shrink-0">
                      <span className="sr-only">{featuredPost.author?.name || 'Author'}</span>
                      <div className="h-10 w-10 rounded-full bg-[#2C5282] flex items-center justify-center text-white">
                        {featuredPost.author?.name?.charAt(0) || 'A'}
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-[#1A365D]">{featuredPost.author?.name || 'Author'}</p>
                      <div className="flex space-x-1 text-sm text-gray-500">
                        <time dateTime={featuredPost.date}>{formatDate(featuredPost.date)}</time>
                        <span aria-hidden="true">&middot;</span>
                        <span>{featuredPost.readTime}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Link
                      to={`/blogdetails/${featuredPost.slug}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2C5282] hover:bg-[#1A365D] transition-colors duration-200"
                    >
                      Read Full Article
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Blog Posts Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <motion.h2 
              className="text-3xl font-extrabold text-[#2C5282] mb-4 md:mb-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Latest Articles
            </motion.h2>
            
            <motion.div 
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {blogCategories.map((category) => (
                <button
                  key={category._id}
                  onClick={() => setFilter(category.identifier)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    filter === category.identifier
                      ? 'bg-[#2C5282] text-white'
                      : 'bg-white text-gray-700 hover:bg-blue-50'
                  } transition-colors duration-200`}
                >
                  {category.name}
                </button>
              ))}
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((post, index) => (
              <motion.div
                key={post._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <Link to={`/blogdetails/${post.slug}`}>
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                </Link>
                <div className="p-6">
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-[#2C5282]">
                      {getCategoryName(post.category)}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{formatDate(post.date)}</span>
                  </div>
                  
                  <Link 
                    to={`/blogdetails/${post.slug}`}
                    className="block mt-2 text-xl font-semibold text-[#1A365D] hover:text-[#2C5282] transition-colors duration-200"
                  >
                    {post.title}
                  </Link>
                  
                  <p className="mt-3 text-gray-600 line-clamp-3">{post.excerpt}</p>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="sr-only">{post.author?.name || 'Author'}</span>
                        <div className="h-8 w-8 rounded-full bg-[#2C5282] flex items-center justify-center text-white">
                          {post.author?.name?.charAt(0) || 'A'}
                        </div>
                      </div>
                      <div className="ml-2">
                        <p className="text-sm font-medium text-[#1A365D]">{post.author?.name || 'Author'}</p>
                      </div>
                    </div>
                    
                    <Link
                      to={`/blogdetails/${post.slug}`}
                      className="inline-flex items-center text-sm font-medium text-[#2C5282] hover:text-[#1A365D]"
                    >
                      Read more
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredBlogs.length === 0 && (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No articles found</h3>
              <p className="mt-1 text-gray-500">We couldn't find any articles in this category. Please check back later or select another category.</p>
            </div>
          )}
          
          {/* <div className="mt-12 text-center">
            <Link
              to="/blog/all"
              className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              View All Articles
            </Link>
          </div> */}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              className="text-3xl font-extrabold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Stay Updated with Learning Tips
            </motion.h2>
            <motion.p
              className="text-lg text-blue-100 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Subscribe to our newsletter and receive study tips, subject insights, and educational resources for class 8-10 students directly in your inbox.
            </motion.p>
            <motion.div
              className="sm:flex sm:justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="sm:flex-1 max-w-lg mx-auto">
                <form className="sm:flex" onSubmit={handleSubscribe}>
                  <div className="flex-1 min-w-0">
                    <label htmlFor="email" className="sr-only">Email address</label>
                    <input 
                      id="email" 
                      type="email" 
                      placeholder="Enter your email" 
                      className="block w-full px-4 py-3 rounded-md border-0 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300 focus:ring-offset-[#1A365D]"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-3">
                    <button 
                      type="submit" 
                      className="block w-full py-3 px-4 rounded-md shadow bg-white text-[#2C5282] font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#1A365D] sm:text-sm disabled:opacity-70"
                      disabled={subscribing}
                    >
                      {subscribing ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                </form>
                <p className="mt-3 text-sm text-blue-100">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Tags Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <motion.h2 
              className="text-2xl font-bold text-[#2C5282]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Popular Topics
            </motion.h2>
          </div>
          
          <motion.div
            className="flex flex-wrap justify-center gap-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {popularTags.map((tag, index) => (
              <Link
                key={index}
                to={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-4 py-2 rounded-full bg-gray-100 text-sm font-medium text-gray-700 hover:bg-blue-100 hover:text-[#2C5282] transition-colors duration-200"
              >
                {tag}
              </Link>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;